import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import CompanyDashboardPage from './pages/CompanyDashboardPage';
import CompaniesPage from './pages/CompaniesPage';
import EvaluatorsPage from './pages/EvaluatorsPage';
import ComplaintsPage from './pages/ComplaintsPage';
import ComplaintDetailPage from './pages/ComplaintDetailPage';
import CompanyDeductionsPage from './pages/CompanyDeductionsPage';
import SLAConfigurationPage from './pages/SLAConfigurationPage';
import ReportsPage from './pages/ReportsPage';
import EvaluatorReportsPage from './pages/EvaluatorReportsPage';
import CompanyPerformancePage from './pages/CompanyPerformancePage';
import CompanyManagersPage from './pages/CompanyManagersPage';
import CompanyViolationsPage from './pages/CompanyViolationsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import NotificationProvider from './components/NotificationProvider';
import apiService from './services/api';
import './index.css';

function App() {
  const [userType, setUserType] = useState(() => {
    const user = apiService.getCurrentUser();
    return user?.userType || '';
  });

  useEffect(() => {
    // Listen for storage changes (login/logout events)
    const handleStorageChange = () => {
      const user = apiService.getCurrentUser();
      setUserType(user?.userType || '');
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom event for same-window updates
    const handleAuthChange = () => {
      const user = apiService.getCurrentUser();
      setUserType(user?.userType || '');
    };

    window.addEventListener('auth-change', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-change', handleAuthChange);
    };
  }, []);

  // Determine home route based on user type
  const getHomeRoute = () => {
    if (userType === 'admin') {
      return '/dashboard';
    } else if (userType === 'company_manager' || userType === 'company_owner') {
      return '/company-dashboard';
    }
    return '/complaints';
  };

  return (
    <I18nextProvider i18n={i18n}>
      <NotificationProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

          {/* Admin Dashboard (Admin only) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout userType={userType}>
                  <DashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Company Dashboard (Company Owner & Company Manager) */}
          <Route
            path="/company-dashboard"
            element={
              <ProtectedRoute allowedRoles={['company_owner', 'company_manager']}>
                <AppLayout userType={userType}>
                  <CompanyDashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Companies (Admin only) */}
          <Route
            path="/companies"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout userType={userType}>
                  <CompaniesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Evaluators (Admin only) */}
          <Route
            path="/evaluators"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout userType={userType}>
                  <EvaluatorsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Deductions (Admin only) */}
          <Route
            path="/deductions"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout userType={userType}>
                  <CompanyDeductionsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* SLA Configuration (Admin only) */}
          <Route
            path="/sla-config"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout userType={userType}>
                  <SLAConfigurationPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Reports (Admin only) */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout userType={userType}>
                  <ReportsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Evaluator Reports (Admin only) */}
          <Route
            path="/evaluator-reports"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout userType={userType}>
                  <EvaluatorReportsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Company Performance (Admin only) */}
          <Route
            path="/company-performance"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AppLayout userType={userType}>
                  <CompanyPerformancePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Complaints (Admin, Company Owner & Company Manager) */}
          <Route
            path="/complaints"
            element={
              <ProtectedRoute allowedRoles={['admin', 'company_owner', 'company_manager']}>
                <AppLayout userType={userType}>
                  <ComplaintsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/complaints/:id"
            element={
              <ProtectedRoute allowedRoles={['admin', 'company_owner', 'company_manager']}>
                <AppLayout userType={userType}>
                  <ComplaintDetailPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* Managers (Company Owner only) */}
          <Route
            path="/managers"
            element={
              <ProtectedRoute allowedRoles={['company_owner']}>
                <AppLayout userType={userType}>
                  <CompanyManagersPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

          {/* My Violations (Company Owner & Company Manager) */}
          <Route
            path="/my-violations"
            element={
              <ProtectedRoute allowedRoles={['company_owner', 'company_manager']}>
                <AppLayout userType={userType}>
                  <CompanyViolationsPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />

            {/* Default redirect based on role */}
            <Route path="/" element={<Navigate to={getHomeRoute()} replace />} />
          </Routes>
        </Router>
      </NotificationProvider>
    </I18nextProvider>
  );
}

export default App;
